import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { LIGHTING } from '../constants';
import type { WorldType } from '../types';
import { lerp, hexToRgb } from '../utils';

interface WorldLightingProps {
  world: WorldType;
  transitionProgress?: number;
}

export function WorldLighting({
  world,
  transitionProgress = world === 'real' ? 0 : 1,
}: WorldLightingProps) {
  const hemisphereRef = useRef<THREE.HemisphereLight>(null);
  const directionalRef = useRef<THREE.DirectionalLight>(null);

  useFrame(() => {
    if (!hemisphereRef.current || !directionalRef.current) return;

    const t = transitionProgress;
    const realLight = LIGHTING.real;
    const spectralLight = LIGHTING.spectral;

    // Interpolate hemisphere light (sky + ground colors)
    const skyColor = lerpColor3(
      realLight.ambient.color,
      spectralLight.ambient.color,
      t
    );
    const groundColor = lerpColor3(
      realLight.ambient.groundColor,
      spectralLight.ambient.groundColor,
      t
    );
    hemisphereRef.current.color.setRGB(skyColor.r, skyColor.g, skyColor.b);
    hemisphereRef.current.groundColor.setRGB(groundColor.r, groundColor.g, groundColor.b);
    hemisphereRef.current.intensity = lerp(
      realLight.ambient.intensity,
      spectralLight.ambient.intensity,
      t
    );

    // Interpolate directional light
    const dirColor = lerpColor3(
      realLight.directional.color,
      spectralLight.directional.color,
      t
    );
    directionalRef.current.color.setRGB(dirColor.r, dirColor.g, dirColor.b);
    directionalRef.current.intensity = lerp(
      realLight.directional.intensity,
      spectralLight.directional.intensity,
      t
    );

    // Interpolate position
    directionalRef.current.position.set(
      lerp(realLight.directional.position[0], spectralLight.directional.position[0], t),
      lerp(realLight.directional.position[1], spectralLight.directional.position[1], t),
      lerp(realLight.directional.position[2], spectralLight.directional.position[2], t)
    );
  });

  const config = world === 'real' ? LIGHTING.real : LIGHTING.spectral;

  return (
    <>
      {/* Hemisphere light for Monument Valley style (sky + ground reflection) */}
      <hemisphereLight
        ref={hemisphereRef}
        args={[config.ambient.color, config.ambient.groundColor, config.ambient.intensity]}
      />

      {/* Subtle directional light for soft shadows */}
      <directionalLight
        ref={directionalRef}
        color={config.directional.color}
        intensity={config.directional.intensity}
        position={config.directional.position}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-camera-far={40}
        shadow-camera-left={-15}
        shadow-camera-right={15}
        shadow-camera-top={15}
        shadow-camera-bottom={-15}
        shadow-bias={-0.001}
        shadow-radius={8}
      />
    </>
  );
}

function lerpColor3(colorA: string, colorB: string, t: number) {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  return {
    r: lerp(a.r, b.r, t),
    g: lerp(a.g, b.g, t),
    b: lerp(a.b, b.b, t),
  };
}
